import { CohortMetricId, RegularMetricId } from '../../../api/types-graphql';
import {
  LanguageString,
  StoryboardComponentDependencies,
} from '../../../common/components/dashboards/types';
import {
  KpiFactboxBenchmarkPeriods,
  KpiFactboxBenchmarkTypes,
} from '../../../common/components/data-viz-containers/common/types';
import { KpiFactboxSingleMetricVizViews } from '../../../common/components/data-viz-containers/kpi-factbox-single-metric/types';
import {
  StoryboardComponentIds,
  StoryboardItemConfig,
} from '../../../common/components/storyboard-components/common/types';
import { FactboxFontSizes } from '../../../common/components/ui-components/data-viz-ui/factboxes/common/types';
import { Languages } from '../../../constants';
import {
  DataTypes,
  EmployeeDataFields,
  MetricIdType,
  SegmentBy,
  SingleValueTimeSelection,
} from '../../../types';
import { getFiscalYearTimeSelection } from '../utils';

const MALE_GENDER_SQL_FILTER = `EMPLOYEE.GENDER = 'Male'`;

const TD_HOLDINGS_HC_SEGMENT_BY: SegmentBy = {
  type: 'non-hierarchical',
  dataField: {
    dataType: DataTypes.EMPLOYEE,
    dataField: EmployeeDataFields.CUSTOM_FIELD_1_LEVEL_1,
  },
};

const TD_HOLDINGS_HC_COHORT_SEGMENT_BY: SegmentBy = {
  type: 'employee-cohort',
  dataField: {
    dataType: DataTypes.EMPLOYEE,
    dataField: EmployeeDataFields.CUSTOM_FIELD_1_LEVEL_1,
  },
};

const yoyDeltaBenchmark = [
  {
    benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
    benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
  },
];

export const getTdHoldingsHumanCapitalStoryboardComponents = ({
  storyboardDateUtils,
  domainPreferences,
}: StoryboardComponentDependencies): StoryboardItemConfig[] => {
  const fiscalYearTimeSelection = getFiscalYearTimeSelection(
    storyboardDateUtils,
    domainPreferences
  );

  const layoutCell = { gridColSpan: 1, gridRowSpan: 1 };

  const factbox = (args: {
    chartId: string;
    title: LanguageString;
    subtitle?: LanguageString;
    footer?: LanguageString;
    metric: MetricIdType;
    timeSelection: SingleValueTimeSelection;
    benchmarkConfigs?: typeof yoyDeltaBenchmark;
    filters?: string;
    /**
     * Cohort metrics are not joined to the employee table; segmenting by employee
     * fields (e.g. CUSTOM_FIELD_1_LEVEL_1) breaks cohort metric queries. Set to
     * false for pure cohort metrics and provide segmentByOverride with a
     * cohort-table field instead.
     */
    includeEmployeeSegmentBy?: boolean;
    segmentByOverride?: SegmentBy;
  }): StoryboardItemConfig => ({
    chartId: args.chartId,
    layoutConfig: layoutCell,
    componentConfig: {
      componentId: StoryboardComponentIds.FACTBOX_SINGLE_METRIC,
      dataConfig: {
        title: args.title,
        subtitle: args.subtitle,
        footer: args.footer,
        fontSize: FactboxFontSizes.SMALL,
        section: {
          query: {
            metric: args.metric,
            timeSelection: args.timeSelection,
            filters: args.filters,
            segmentBy:
              args.segmentByOverride ??
              (args.includeEmployeeSegmentBy !== false
                ? TD_HOLDINGS_HC_SEGMENT_BY
                : undefined),
          },
          benchmarkConfigs: args.benchmarkConfigs,
        },
      },
      selectedView: KpiFactboxSingleMetricVizViews.FACTBOX,
    },
  });

  return [
    factbox({
      chartId: 'td-hc-headcount',
      title: {
        [Languages.EN]: 'Headcount',
        [Languages.JA]: '従業員数',
      },
      subtitle: {
        [Languages.EN]: 'As of fiscal year-end',
        [Languages.JA]: '年度末日時点',
      },
      metric: {
        type: 'RegularMetricId',
        value: RegularMetricId.Pm0001_1IndividualsCount,
      },
      timeSelection: fiscalYearTimeSelection,
      benchmarkConfigs: yoyDeltaBenchmark,
    }),
    factbox({
      chartId: 'td-hc-female-manager-ratio',
      title: {
        [Languages.EN]: 'Female Manager Ratio',
        [Languages.JA]: '女性管理職比率',
      },
      subtitle: {
        [Languages.EN]: 'As of April 1',
        [Languages.JA]: '4月1日時点',
      },
      metric: {
        type: 'CohortMetricId',
        value: CohortMetricId.Pm0445_2CltFMngJpn0000033PercMngDef1,
      },
      timeSelection: fiscalYearTimeSelection,
      benchmarkConfigs: yoyDeltaBenchmark,
      includeEmployeeSegmentBy: false,
      segmentByOverride: TD_HOLDINGS_HC_COHORT_SEGMENT_BY,
    }),
    factbox({
      chartId: 'td-hc-gender-pay-gap',
      title: {
        [Languages.EN]: 'Gender Pay Gap',
        [Languages.JA]: '男女間賃金差異',
      },
      footer: {
        [Languages.EN]: 'Including payments to leavers',
        [Languages.JA]: '※退職者支給分も含む',
      },
      metric: {
        type: 'RegularMetricId',
        value: RegularMetricId.Pm0394_1CstmGenderPayGapFToMTotalCompAvg1BsPerc,
      },
      timeSelection: fiscalYearTimeSelection,
      benchmarkConfigs: yoyDeltaBenchmark,
    }),
    factbox({
      chartId: 'td-hc-male-parental-leave-utilization',
      title: {
        [Languages.EN]: 'Male Parental Leave Utilization',
        [Languages.JA]: '男性育休取得率',
      },
      metric: {
        type: 'RegularMetricId',
        value: RegularMetricId.Pm0171ParentalLeaveTakenUtilizationPerc,
      },
      timeSelection: fiscalYearTimeSelection,
      benchmarkConfigs: yoyDeltaBenchmark,
      filters: MALE_GENDER_SQL_FILTER,
    }),
    factbox({
      chartId: 'td-hc-disability-employment-rate',
      title: {
        [Languages.EN]: 'Disability Employment Rate',
        [Languages.JA]: '障がい者雇用率',
      },
      metric: {
        type: 'RegularMetricId',
        value: RegularMetricId.Pm0198_7DisabilityPopulationWeightedPercOfFte,
      },
      timeSelection: fiscalYearTimeSelection,
      benchmarkConfigs: yoyDeltaBenchmark,
    }),
    factbox({
      chartId: 'td-hc-absent-days-rate',
      title: {
        [Languages.EN]: 'Absent Days Rate',
        [Languages.JA]: '欠勤率',
      },
      metric: {
        type: 'RegularMetricId',
        value: RegularMetricId.Pm0212_2AbsentDaysPercOfWorkingDays,
      },
      timeSelection: fiscalYearTimeSelection,
      benchmarkConfigs: yoyDeltaBenchmark,
    }),
    factbox({
      chartId: 'td-hc-male-parental-leave-days',
      title: {
        [Languages.EN]: 'Days of Parental Leave Taken (Male)',
        [Languages.JA]: '育休取得日数(男性)',
      },
      metric: {
        type: 'RegularMetricId',
        value: RegularMetricId.Pm0183DaysOfParentalLeaveTakenAvg,
      },
      timeSelection: fiscalYearTimeSelection,
      benchmarkConfigs: yoyDeltaBenchmark,
      filters: MALE_GENDER_SQL_FILTER,
    }),
    factbox({
      chartId: 'td-hc-annual-paid-leave',
      title: {
        [Languages.EN]: 'Annual Paid Leave (avg. days)',
        [Languages.JA]: '有給休暇平均取得日数',
      },
      metric: {
        type: 'RegularMetricId',
        value: RegularMetricId.Pm0217_4StatutoryAnnualPaidLeaveSumPerEligibleHc,
      },
      timeSelection: fiscalYearTimeSelection,
      benchmarkConfigs: yoyDeltaBenchmark,
    }),
    factbox({
      chartId: 'td-hc-natal-leaves-count',
      title: {
        [Languages.EN]: 'Natal Leaves Taken',
        [Languages.JA]: '産前産後休暇取得者数',
      },
      metric: {
        type: 'RegularMetricId',
        value: RegularMetricId.Pm0450_3PrePostNatalLeaveUtilizationCountNum,
      },
      timeSelection: fiscalYearTimeSelection,
      benchmarkConfigs: yoyDeltaBenchmark,
    }),
    factbox({
      chartId: 'td-hc-caregiving-leaves-count',
      title: {
        [Languages.EN]: 'Caregiving Leave Takers',
        [Languages.JA]: '介護休業取得者数',
      },
      metric: {
        type: 'RegularMetricId',
        value: RegularMetricId.Pm0451_5CaregivLeaveDaysEmpCount,
      },
      timeSelection: fiscalYearTimeSelection,
      benchmarkConfigs: yoyDeltaBenchmark,
    }),
  ];
};
